import { useCallback, useEffect, useState } from 'react';

import { Stack, Tooltip } from '@mui/material';
import { Location } from 'history';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';

import Button from 'domains/core/components/Button';
import RouteLeavingGuard, { DialogProps } from 'domains/core/components/RouteLeavingGuard';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { URLPaths } from 'models/enums';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText/ContentStatusText';

import { VoiceContentAction, VoiceContentInteraction, VoiceContentStateTypes, VoiceText } from '../types';

const MIN_MESSAGE_LENGTH = 8;
const VALID_PHONE_NUMBER_LENGTHS = [10, 11];

const voiceContentDialogProps: DialogProps = {
    title: `Don't lose your work!`,
    contentText: `Before you go, it looks like you've made some changes. If you exit without saving, any changes you've made will be lost.`,
};

type Props = {
    handleSave: (status?: ContentStatus) => void;
    isLoading: boolean;
    voiceContentState: VoiceContentStateTypes;
};

const SaveAndExitButton = ({ handleSave, isLoading, voiceContentState }: Props) => {
    const { contentDraft: hasContentDraft } = useFlags();
    const { canPublishContent, canManageContent } = useAccessCentrePermissions();
    const [isValidContent, setIsValidContent] = useState(false);
    const [isSaveClicked, setIsSaveClicked] = useState(false);
    const [currentAction, setCurrentAction] = useState<'saving' | 'publishing' | null>(null);
    const history = useHistory();

    const getIsNavigationBlocked = (location: Location): boolean =>
        !location.pathname.includes(URLPaths.CONTENT_CREATE_VOICE_CALL) && !isSaveClicked;

    const handleSaveAndExitClick = () => {
        setIsSaveClicked(true);
        setCurrentAction('saving');
        if (hasContentDraft) {
            handleSave(ContentStatus.DRAFT);
        } else {
            handleSave();
        }
    };

    const handlePublishClick = () => {
        setIsSaveClicked(true);
        setCurrentAction('publishing');
        if (hasContentDraft) {
            handleSave(ContentStatus.PUBLISHED);
        } else {
            handleSave();
        }
    };

    const getIsValidInteraction = useCallback((interaction: VoiceContentInteraction) => {
        if (!interaction.keypadOption) return false;

        const actionType = interaction?.type;
        if (!actionType) return false;

        const isTransfer = actionType === VoiceContentAction.TRANSFER;

        if (isTransfer) {
            const transferNumber = interaction?.transferNumber;
            const digitMatches = transferNumber?.match(/\d/g);
            const digitOnlyNumber = digitMatches ? digitMatches.join('') : '';

            return !!transferNumber && VALID_PHONE_NUMBER_LENGTHS.includes(digitOnlyNumber.length);
        } else {
            return (
                !!(interaction?.message?.[0] as VoiceText)?.text &&
                (interaction?.message?.[0] as VoiceText)?.text.length > MIN_MESSAGE_LENGTH
            );
        }
    }, []);

    const getIsValidContent = useCallback(() => {
        const introductionMessage = voiceContentState.voiceJson.voiceCall.message[0];
        const hasIntroductionMessage = !!(
            introductionMessage.type === 'text' && introductionMessage.text.length > MIN_MESSAGE_LENGTH
        );

        const hasValidInteractions = voiceContentState.voiceJson.voiceCall.interactions.every(getIsValidInteraction);

        return hasIntroductionMessage && hasValidInteractions;
    }, [voiceContentState, getIsValidInteraction]);

    useEffect(() => {
        const callIsValid = getIsValidContent();
        setIsValidContent(callIsValid);
    }, [getIsValidContent, voiceContentState]);

    // Reset currentAction when loading is complete
    useEffect(() => {
        if (!isLoading) {
            setCurrentAction(null);
        }
    }, [isLoading]);

    const isSaveDisabled = !isValidContent || isLoading;
    const getDisabledMessage = () => {
        if (!isSaveDisabled) return '';
        if (isLoading && currentAction === 'publishing') return 'Content is publishing';
        if (isLoading && currentAction === 'saving') return 'Content is saving';
        if (isLoading) return 'Content is saving';
        if (!isValidContent) return 'Content must have an introduction message and no invalid interactions';
    };
    const disabledMessage = getDisabledMessage();

    return (
        <>
            <RouteLeavingGuard
                dialogProps={voiceContentDialogProps}
                shouldBlockNavigation={getIsNavigationBlocked}
                onConfirm={() => history.push(URLPaths.CONTENT)}
            />
            <Tooltip title={disabledMessage}>
                <Stack direction="row" alignItems="center" columnGap={1} ml={1}>
                    {canManageContent && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleSaveAndExitClick}
                            disabled={isSaveDisabled}
                            data-testid="save-exit-button"
                            role="button"
                        >
                            Save & Exit
                        </Button>
                    )}

                    {hasContentDraft && canPublishContent && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePublishClick}
                            disabled={isSaveDisabled}
                            data-testid="publish-button"
                            role="button"
                        >
                            Publish
                        </Button>
                    )}
                </Stack>
            </Tooltip>
        </>
    );
};

export default SaveAndExitButton;
